import React from 'react';
import { Link } from 'react-router-dom';

const RetirementPlannerInfoPage: React.FC = () => {
  return (
    <div className="retire-info-page">
      <div className="hero-section retirement">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Advanced Retirement Planner</h1>
            <p className="hero-description">Plan retirement with taxes, RRIF rules, OAS clawback, investment returns, and more. Get clear guidance and actionable outputs.</p>
            <div className="cta-buttons">
              <Link to="/rrsp" className="btn btn-primary btn-large">Open Planner</Link>
              <Link to="/" className="btn btn-secondary btn-large">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="features-section">
        <div className="container">
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature-card"><div className="feature-icon">📊</div><h3>Detailed Cash Flows</h3><p>Monthly/annual projections with contributions and withdrawals.</p></div>
            <div className="feature-card"><div className="feature-icon">🧾</div><h3>Tax Engine</h3><p>Progressive rates, credits, RRSP deductions and TFSA handling.</p></div>
            <div className="feature-card"><div className="feature-icon">🧓</div><h3>OAS & RRIF</h3><p>Clawback modeling and withdrawal schedules.</p></div>
            <div className="feature-card"><div className="feature-icon">💡</div><h3>Guidance</h3><p>Friendly helper text to choose realistic assumptions.</p></div>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Features by Tier</h2>
          <div className="pricing-grid">
            <div className="pricing-card free"><h3>Free</h3><div className="price">$0</div><ul><li>✅ Core projections</li><li>✅ Charts & tables</li><li>✅ Built‑in guidance</li></ul><Link to="/rrsp" className="btn btn-primary">Start Free</Link></div>
            <div className="pricing-card account"><h3>Account</h3><div className="price">Free</div><ul><li>✅ Save scenarios</li><li>✅ Load/update scenarios</li></ul><Link to="/account" className="btn btn-secondary">Create Account</Link></div>
            <div className="pricing-card supporter"><h3>Supporters Club</h3><div className="price">$10/year</div><ul><li>✅ Everything in Free & Account</li><li>✅ Advanced optimizations (roadmap)</li><li>✅ Early access</li></ul><Link to="/account" className="btn btn-premium">Join Supporters Club</Link></div>
          </div>
        </div>
      </section>

      <section className="guide-section">
        <div className="container">
          <h2>Complete Guide: CPP/OAS Optimization & Retirement Planning in Canada</h2>

          <div className="guide-content">
            <h3>The $200,000 Decision: When Should You Take CPP?</h3>
            <p>When you start CPP can make a $150,000-$250,000 difference in lifetime benefits. Most Canadians take it at 65 by default, but that's often the wrong choice. Here's the math:</p>

            <h4>CPP at Age 60 vs 65 vs 70: Real Numbers</h4>
            <p><strong>Assumptions:</strong> You qualify for maximum CPP ($15,678/year at age 65 in 2025)</p>

            <h5>Option 1: Start CPP at Age 60</h5>
            <ul>
              <li>Reduced by 36% (0.6% per month for 60 months early) = $10,034/year</li>
              <li>From age 60-85 (25 years): $10,034 × 25 = $250,850 total</li>
              <li>Monthly payment: $836</li>
              <li><strong>Break-even vs age 65:</strong> Age 74</li>
            </ul>

            <h5>Option 2: Start CPP at Age 65 (Standard)</h5>
            <ul>
              <li>Full amount: $15,678/year</li>
              <li>From age 65-85 (20 years): $15,678 × 20 = $313,560 total</li>
              <li>Monthly payment: $1,306</li>
            </ul>

            <h5>Option 3: Delay CPP to Age 70</h5>
            <ul>
              <li>Increased by 42% (0.7% per month for 60 months delay) = $22,263/year</li>
              <li>From age 70-85 (15 years): $22,263 × 15 = $333,945 total</li>
              <li>Monthly payment: $1,855</li>
              <li><strong>Break-even vs age 65:</strong> Age 82</li>
            </ul>

            <p><strong>Verdict if you live to 85:</strong> Delaying to 70 wins by $20,000+. Taking at 60 loses $63,000 vs starting at 65.</p>
            <p><strong>BUT:</strong> If you die at 75, taking at 60 wins by $90,000. This is why your health, family longevity, and need for income TODAY matter enormously.</p>

            <h3>When Taking CPP at 60 Makes Sense</h3>

            <h4>Scenario 1: Health Issues or Poor Family Longevity</h4>
            <p>If you have serious health conditions or family history shows average lifespan under 75, taking CPP early maximizes your lifetime benefits. A bird in hand is worth two in the bush—$10k/year now beats $22k/year that you might not live to collect.</p>

            <h4>Scenario 2: You're Unemployed Age 60-64 with No Savings</h4>
            <p>If you lose your job at 60 and have limited savings, CPP provides immediate income to avoid depleting RRSPs (which triggers high taxes) or going into debt. The math favors delaying, but cashflow reality matters more.</p>

            <h4>Scenario 3: You Have Significant Other Income Sources</h4>
            <p>Counterintuitive but true: If you have a defined benefit pension paying $50k/year, taking CPP early while in a lower tax bracket (age 60-64) can be smarter than delaying and getting pushed into higher brackets later. Our Monte Carlo calculator models this precisely.</p>

            <h3>When Delaying CPP to 70 Makes Sense</h3>

            <h4>Scenario 1: You're Still Working Age 65-70</h4>
            <p>If you're earning $80k+/year working age 65-70, adding CPP income just gets taxed at your marginal rate (35-45%). Better to delay CPP, live off work income, and start CPP at 70 when you fully retire. The 42% increase is like getting a guaranteed 8.4% annual return.</p>

            <h4>Scenario 2: Strong Family Longevity (Expect to Live to 85+)</h4>
            <p>If your parents/grandparents lived to 85-95 and you're in good health, delaying maximizes lifetime benefits. The break-even is age 82—every year after that is pure profit from the 42% boost.</p>

            <h4>Scenario 3: You Have Sufficient RRSP/TFSA to Bridge to Age 70</h4>
            <p>If you have $400k+ in RRSPs/TFSAs, you can draw down these accounts age 65-70 while delaying CPP. Benefit: You reduce RRSP balances before RRIF forced withdrawals begin at 72, potentially avoiding OAS clawback later.</p>

            <h3>OAS: The $15,000/Year Clawback Trap</h3>
            <p>Old Age Security pays $7,707/year (2025, ages 65-74) or $8,478/year (ages 75+) BUT it claws back 15¢ per dollar of income over $90,997. Full clawback occurs at $148,451 income.</p>

            <h4>Real Example: OAS Clawback in Action</h4>
            <p><strong>Your income at age 70:</strong></p>
            <ul>
              <li>CPP: $22,263 (delayed to 70)</li>
              <li>DB Pension: $45,000</li>
              <li>RRIF withdrawal: $35,000</li>
              <li><strong>Total income: $102,263</strong></li>
            </ul>
            <p>Income over threshold: $102,263 - $90,997 = $11,266</p>
            <p>OAS clawback: $11,266 × 15% = $1,690/year LOST</p>
            <p><strong>Effective OAS benefit:</strong> $7,707 - $1,690 = $6,017/year</p>

            <h4>Strategies to Avoid OAS Clawback</h4>

            <h5>1. Withdraw RRSP Aggressively Before Age 65</h5>
            <p>Pull money out of RRSPs age 60-64 to reduce balances before RRIF withdrawals become mandatory at 72. Lower RRIF withdrawals = lower income = less OAS clawback.</p>
            <p><strong>Example:</strong> You have $600k in RRSPs at age 60. Instead of waiting, withdraw $80k/year age 60-64 (paying 35% tax). This reduces your RRSP to $200k by age 65. At 72, mandatory RRIF withdrawals are only $10,700/year (5.35% rate) instead of $32,100/year if you'd kept $600k. Savings on OAS clawback: $3,200/year for life.</p>

            <h5>2. Shift Income to Spouse</h5>
            <p>If one spouse has significantly higher income, pension income splitting can keep both below the OAS threshold. You can split up to 50% of eligible pension income, lowering the high earner's income and preserving OAS.</p>

            <h5>3. Prioritize TFSA Over RRSP in Retirement</h5>
            <p>TFSA withdrawals don't count as income. If you have $100k in TFSA and $100k in RRSP, withdraw from TFSA first to keep taxable income low and avoid OAS clawback.</p>

            <h3>RRSP vs TFSA Withdrawal Strategy</h3>

            <h4>The RRIF Time Bomb</h4>
            <p>At age 72 (changed from 71 in 2023), RRSPs must convert to RRIFs with mandatory minimum withdrawals:</p>
            <ul>
              <li><strong>Age 72:</strong> 5.35% minimum withdrawal</li>
              <li><strong>Age 75:</strong> 6.02%</li>
              <li><strong>Age 80:</strong> 7.38%</li>
              <li><strong>Age 85:</strong> 9.58%</li>
              <li><strong>Age 90:</strong> 14.49%</li>
            </ul>

            <p><strong>The problem:</strong> If you have $800k in RRIFs at age 72, you MUST withdraw $42,800 that year (5.35%). Combined with CPP ($22k) and OAS ($8k), your income is $72,800—all taxable. By age 85, forced RRIF withdrawals alone are $76,640/year, pushing you deep into OAS clawback territory.</p>

            <h4>Smart Withdrawal Sequence</h4>

            <p><strong>Age 60-64 (Early Retirement):</strong></p>
            <ol>
              <li>Draw from non-registered accounts first (only pay tax on capital gains/dividends)</li>
              <li>Supplement with RRSP withdrawals strategically to stay in low tax brackets</li>
              <li>Leave TFSA untouched—it's the most valuable asset</li>
            </ol>

            <p><strong>Age 65-71 (OAS Started, Before RRIF):</strong></p>
            <ol>
              <li>Aggressively draw down RRSPs to reduce future RRIF balances</li>
              <li>Target income just below OAS clawback threshold ($90k)</li>
              <li>Use TFSA to top up income without triggering clawback</li>
            </ol>

            <p><strong>Age 72+ (RRIF Mandatory Withdrawals):</strong></p>
            <ol>
              <li>Take RRIF minimum withdrawals (required by law)</li>
              <li>Use TFSA for any additional income needs</li>
              <li>Consider pension income splitting with spouse to minimize OAS clawback</li>
            </ol>

            <h3>Monte Carlo Retirement Planning: Why Averages Lie</h3>
            <p>Traditional retirement calculators use average returns (e.g., "assume 6% annually"). This is dangerously misleading because sequence of returns matters.</p>

            <h4>Example: Why Sequence of Returns Matters</h4>

            <p><strong>Scenario A (Good Sequence):</strong></p>
            <p>Start with $500k, withdraw $35k/year</p>
            <ul>
              <li>Year 1: +15% return → $540k</li>
              <li>Year 2: +12% return → $570k</li>
              <li>Year 3: +8% return → $580k</li>
              <li>Years 4-10: -10%, +5%, +7%, -8%, +9%, +11%, +6%</li>
              <li><strong>Age 75 balance: $485k remaining</strong></li>
            </ul>

            <p><strong>Scenario B (Bad Sequence - SAME average return, different order):</strong></p>
            <p>Start with $500k, withdraw $35k/year</p>
            <ul>
              <li>Year 1: -10% return → $415k</li>
              <li>Year 2: -8% return → $347k</li>
              <li>Year 3: +6% return → $333k</li>
              <li>Years 4-10: +15%, +12%, +8%, +5%, +7%, +9%, +11%</li>
              <li><strong>Age 75 balance: $398k remaining</strong></li>
            </ul>

            <p><strong>Result:</strong> Same average returns, same withdrawal rate, but $87k difference in outcomes because of bad returns early in retirement. This is why Monte Carlo simulation matters—it tests thousands of possible return sequences to show you the range of outcomes.</p>

            <h4>How Our Monte Carlo Calculator Works</h4>
            <p>Instead of assuming "6% every year," we run 10,000 simulations with realistic return distributions:</p>
            <ul>
              <li>Some simulations: Market crashes early (2008-like scenario)</li>
              <li>Some simulations: Market booms early (1990s-like scenario)</li>
              <li>Most simulations: Mixed results (typical markets)</li>
            </ul>

            <p>Then we show you:</p>
            <ul>
              <li><strong>25th percentile outcome:</strong> You'd be better than this in 75% of scenarios (bad luck)</li>
              <li><strong>50th percentile outcome:</strong> Median result (typical luck)</li>
              <li><strong>75th percentile outcome:</strong> You'd reach this in 25% of scenarios (good luck)</li>
            </ul>

            <p>This tells you: "If markets cooperate, you'll have $1.2M at age 85. If markets are rough, you'll have $600k. Plan for the 25th percentile to be safe."</p>

            <h3>Real Retirement Scenarios with Numbers</h3>

            <h4>Scenario 1: Single Person, $65k Pension, $300k RRSP, Age 62</h4>
            <p><strong>Decision:</strong> When to take CPP? How to avoid OAS clawback?</p>

            <p><strong>Analysis:</strong></p>
            <ul>
              <li>Current income: $65k pension</li>
              <li>Max CPP at 65: $15,678</li>
              <li>OAS at 65: $7,707</li>
              <li>Combined retirement income: $88,385 (just under OAS threshold of $90,997)</li>
              <li>RRIF at 72 (from $300k): Mandatory withdrawal $16,050</li>
              <li><strong>Total income at 72: $104,435 → OAS clawback of $2,016/year</strong></li>
            </ul>

            <p><strong>Recommendation:</strong></p>
            <ol>
              <li>Take CPP at 65 (standard timing works here)</li>
              <li>Age 62-64: Withdraw $40k/year from RRSP to reduce balance to $180k by age 65</li>
              <li>This reduces RRIF withdrawals at 72 to $9,630, keeping total income at $97,815</li>
              <li>OAS clawback drops to $1,023/year—saving $1,000/year for life</li>
            </ol>

            <h4>Scenario 2: Married Couple, $180k Combined Income, $1.2M RRSPs, Ages 58 & 60</h4>
            <p><strong>Decision:</strong> Early retirement at 60? How to manage massive RRSP without huge tax bills?</p>

            <p><strong>Analysis:</strong></p>
            <ul>
              <li>If both work to 65: Save extra $500k, but RRSPs grow to $1.8M</li>
              <li>At age 72, RRIF minimum on $1.8M = $96,300/year</li>
              <li>Plus CPP ($31k combined) + OAS ($15k) = $142k income</li>
              <li>Tax bracket: 35%+ ($50k in taxes) + full OAS clawback ($15k lost)</li>
              <li><strong>Net retirement income: $77k after tax + clawback</strong></li>
            </ul>

            <p><strong>Alternative strategy:</strong></p>
            <ol>
              <li>Retire at 60, live on $80k/year from RRSP withdrawals age 60-71 (12 years)</li>
              <li>Total withdrawn: $960k over 12 years, taxed at 30% average = $288k taxes paid, $672k net</li>
              <li>RRSP shrinks from $1.2M to $650k by age 72</li>
              <li>RRIF minimum at 72: $34,775 (much lower)</li>
              <li>Plus CPP ($31k) + OAS ($15k) = $80k income</li>
              <li>Tax bracket: 25% ($20k taxes), no OAS clawback</li>
              <li><strong>Net retirement income: $60k after tax, BUT you got 5 extra years of freedom (age 60-65)</strong></li>
            </ol>

            <p><strong>Recommendation:</strong> Retire early at 60. The tax savings from shrinking RRSPs + avoiding OAS clawback + 5 extra years of retirement easily outweigh the smaller nest egg. Quality of life wins.</p>

            <h4>Scenario 3: Late Career Saver, Age 50, $120k Income, Only $180k Saved</h4>
            <p><strong>Decision:</strong> Can they retire at 65? What's the gap?</p>

            <p><strong>Analysis:</strong></p>
            <ul>
              <li>Target retirement income: 70% of working income = $84k/year</li>
              <li>CPP at 65: $15,678</li>
              <li>OAS at 65: $7,707</li>
              <li>Gap: $84k - $23k = $61k/year needed from savings</li>
              <li>Current savings: $180k at age 50</li>
              <li>Years to retirement: 15 years</li>
              <li>If they max out RRSP ($31,560/year) for 15 years at 6% return: $948k at age 65</li>
              <li>Withdrawing $61k/year from $948k lasts only 18 years (age 83)—risky</li>
            </ul>

            <p><strong>Recommendations:</strong></p>
            <ol>
              <li>Max RRSP contributions immediately ($31k/year = 26% of income)</li>
              <li>Cut retirement spending target to $72k/year (60% of income, not 70%)</li>
              <li>Gap becomes $49k/year, making savings last to age 90+</li>
              <li>Consider working part-time age 65-70 ($20k/year) to reduce draw on savings</li>
              <li>Delay CPP to 70 for 42% boost, bridging with RRSP withdrawals age 65-70</li>
            </ol>

            <p><strong>Reality check:</strong> They started late. They won't have a luxurious retirement, but with discipline they can retire securely at 65 with moderate lifestyle.</p>

            <h3>Provincial Tax Differences: Where You Retire Matters</h3>

            <p><strong>Retirement income: $70,000 (CPP + OAS + RRIF withdrawals)</strong></p>

            <h4>Ontario</h4>
            <ul>
              <li>Federal tax: $8,667</li>
              <li>Provincial tax: $3,298</li>
              <li>Total tax: $11,965 (17.1%)</li>
              <li><strong>After-tax income: $58,035</strong></li>
            </ul>

            <h4>Alberta (No Provincial Tax)</h4>
            <ul>
              <li>Federal tax: $8,667</li>
              <li>Provincial tax: $2,898</li>
              <li>Total tax: $11,565 (16.5%)</li>
              <li><strong>After-tax income: $58,435 (+$400/year vs Ontario)</strong></li>
            </ul>

            <h4>Quebec (Higher Taxes)</h4>
            <ul>
              <li>Federal tax: $8,667</li>
              <li>Provincial tax: $4,857</li>
              <li>Total tax: $13,524 (19.3%)</li>
              <li><strong>After-tax income: $56,476 (-$1,559/year vs Ontario)</strong></li>
            </ul>

            <h4>British Columbia (Middle Ground)</h4>
            <ul>
              <li>Federal tax: $8,667</li>
              <li>Provincial tax: $3,156</li>
              <li>Total tax: $11,823 (16.9%)</li>
              <li><strong>After-tax income: $58,177</strong></li>
            </ul>

            <p><strong>Over 20 years of retirement:</strong> Moving from Quebec to Alberta saves $31,180 in taxes on $70k income. However, factor in cost of living differences—Alberta has cheaper housing, Quebec has cheaper childcare (irrelevant in retirement), BC has milder winters (lower heating costs).</p>

            <h3>Common Retirement Planning Mistakes</h3>

            <h4>Mistake 1: Assuming You'll Spend Less in Retirement</h4>
            <p>The "70% rule" (you'll need 70% of pre-retirement income) doesn't hold for everyone. Reality: Ages 65-75 (active years) you might spend 90% as you travel and enjoy hobbies. Ages 75-85, spending drops to 60-70% as you slow down. Ages 85+, spending may spike again due to healthcare and long-term care. Plan for variable spending, not a flat 70%.</p>

            <h4>Mistake 2: Ignoring Inflation on Fixed Income</h4>
            <p>CPP and OAS are indexed to inflation, but many pensions are not. A $50k pension today buys $41k worth of goods in 10 years at 2% inflation. If your pension isn't indexed, you're losing 2% purchasing power annually. Build this into your calculations—what feels comfortable at 65 becomes tight at 80.</p>

            <h4>Mistake 3: Paying Off Mortgage vs Investing</h4>
            <p>Should you use savings to pay off your $200k mortgage at 4% interest, or keep it invested earning 6%? Math says invest (6% return &gt; 4% cost). Psychology says pay it off (peace of mind). Compromise: Pay off mortgage if you're conservative and need security, invest if you're disciplined and can handle volatility. There's no universal right answer.</p>

            <h4>Mistake 4: Not Planning for Long-Term Care</h4>
            <p>Long-term care in Canada: $3,500-6,000/month for assisted living, $6,000-10,000/month for nursing home (depends on province). Government covers some costs, but not all. If one spouse needs care for 5 years, that's $216k-600k out of pocket. Most people don't plan for this and it devastates their spouse's retirement. Budget $150k-300k for potential LTC costs or buy LTC insurance in your 50s.</p>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How accurate is the tax calculation?</h3>
              <p>Our calculator uses current Canadian federal and provincial tax brackets, personal amounts, and credits for accurate modeling. We include CPP/EI deductions, basic personal amount ($15,000 federal), age amount (65+), pension income amount, and provincial variations. However, tax rules change annually and vary by province, so results are directionally accurate for planning purposes. For precise tax advice, especially regarding province-specific credits, capital gains treatment, or complex situations, consult a tax professional or CPA. The calculator is excellent for comparing scenarios (e.g., "Is delaying CPP worth it?") even if absolute numbers may differ slightly from your final tax return.</p>
            </div>
            <div className="faq-item">
              <h3>Can I run multiple scenarios?</h3>
              <p>Yes, with a free account you can save and name multiple scenarios (e.g., "Retire at 60," "Retire at 65 + part-time work," "Delay CPP to 70"). This is extremely valuable for comparing different retirement strategies. Run a scenario with CPP at 60, save it, then run another with CPP at 70, save it, and compare side-by-side. You can also share saved scenarios with your spouse or financial advisor to discuss options together.</p>
            </div>
            <div className="faq-item">
              <h3>Should I take CPP at 60, 65, or 70?</h3>
              <p>It depends on your health, income needs, and longevity expectations. Take at 60 if: You need the income immediately, have health issues, or family history suggests shorter lifespan (under 75). Take at 65 if: Standard retirement age, average health, no strong reason to deviate. Delay to 70 if: You're still working (so CPP would just be taxed heavily), have excellent health/family longevity, or sufficient savings to bridge 65-70. Break-even analysis: Taking at 60 vs 65 breaks even at age 74. Taking at 65 vs 70 breaks even at age 82. If you expect to live past these ages, delay pays off. Our Monte Carlo calculator models all three scenarios with your specific financial situation to show which maximizes your lifetime benefits.</p>
            </div>
            <div className="faq-item">
              <h3>What is OAS clawback and how do I avoid it?</h3>
              <p>OAS clawback (officially "recovery tax") takes back 15¢ of every dollar of income over $90,997 (2025). Full OAS disappears at $148,451 income. To minimize: (1) Reduce RRSP balances before age 72 by withdrawing aggressively age 60-71, lowering future RRIF minimum withdrawals. (2) Use TFSA for retirement income—withdrawals don't count as taxable income. (3) Pension income splitting with spouse (up to 50%) to keep both partners below threshold. (4) Delay CPP if you're still earning income age 65-70, reducing total taxable income during OAS years. Every $10k of income you keep under the threshold saves $1,500/year in clawback. Over 20 years of retirement, that's $30k saved.</p>
            </div>
            <div className="faq-item">
              <h3>What is Monte Carlo simulation and why does it matter?</h3>
              <p>Traditional calculators assume average returns every year (e.g., "6% annually"). Monte Carlo runs thousands of simulations with realistic market volatility—some years +20%, some years -15%, most in between. This reveals the RANGE of possible outcomes, not just one average path. Critical because sequence of returns matters: If markets crash early in retirement (like 2008), your portfolio may never recover even if long-term averages work out. Monte Carlo shows: 25th percentile (bad luck), 50th percentile (typical), 75th percentile (good luck). Plan for the 25th percentile to ensure you don't run out of money in bad scenarios. Our calculator runs 10,000 scenarios and shows you the full distribution of outcomes so you can retire with confidence, not just hope.</p>
            </div>
            <div className="faq-item">
              <h3>RRSP vs TFSA in retirement—which to withdraw first?</h3>
              <p>General rule: Age 60-71 withdraw from RRSP to reduce balances before RRIF minimums kick in at 72. Use TFSA sparingly to top up income. Age 72+ take RRIF minimums (required), use TFSA for additional needs. Why: RRSP withdrawals are fully taxable and count toward OAS clawback. TFSA withdrawals are tax-free and don't count as income. Exception: If you're in an unusually low tax year (e.g., age 60-64 with no employment income), pull aggressively from RRSPs to take advantage of low brackets. Save TFSA for high-income years to avoid OAS clawback. Think of TFSA as your "tax shield" for retirement—use it strategically to manage taxable income.</p>
            </div>
            <div className="faq-item">
              <h3>When do RRIF minimum withdrawals start?</h3>
              <p>Age 72 (changed from 71 in 2023). You must convert RRSP to RRIF by December 31 of the year you turn 72, and start minimum withdrawals in that year. Minimum percentages increase with age: 5.35% at 72, 6.02% at 75, 7.38% at 80, 9.58% at 85, 14.49% at 90. On $500k at age 72, you must withdraw $26,750 minimum. By age 85, that becomes $47,900 even if your balance shrinks. The IRS-like logic: Canada wants its tax revenue after decades of RRSP tax deferrals. This is why drawing down RRSPs age 60-71 is smart—you control the withdrawals before the government forces your hand at 72+.</p>
            </div>
            <div className="faq-item">
              <h3>How much do I need to retire in Canada?</h3>
              <p>Rule of thumb: 10-12x your final salary. If you earn $80k and want to maintain that lifestyle, you need $800k-960k saved plus CPP/OAS. More nuanced: Use the 4% withdrawal rule—you can safely withdraw 4% of savings annually (adjusted for inflation) with 95% success of not running out for 30 years. So $800k × 4% = $32k/year + CPP ($16k) + OAS ($8k) = $56k/year retirement income. For $80k income, you'd need $1.3M saved. Reality: Most Canadians retire with $200k-400k saved and rely heavily on CPP/OAS + downsizing home equity. Our calculator shows exactly what YOU need based on your spending, income sources, and time horizon—no generic rules.</p>
            </div>
            <div className="faq-item">
              <h3>Should I pay off my mortgage before retiring?</h3>
              <p>Conventional wisdom says yes (debt-free retirement = peace of mind). Financial math says maybe not. If your mortgage is 4% and investments return 6%, you're better off keeping the mortgage and investing. However, this assumes: (1) You're disciplined enough to actually invest the difference. (2) You can handle market volatility while carrying debt. (3) Your investments actually return 6% (not guaranteed). Most people lack this discipline, so paying off the mortgage "wins" psychologically even if it's suboptimal mathematically. Compromise: Keep a small mortgage ($100k-150k at low rate) if you're financially savvy, pay it off entirely if it helps you sleep at night. No wrong answer—match the decision to your personality.</p>
            </div>
            <div className="faq-item">
              <h3>What about healthcare costs in retirement?</h3>
              <p>Provincial healthcare covers doctor visits and hospital stays, but not: prescriptions (unless 65+ in some provinces), dental, vision, hearing aids, physiotherapy, or long-term care. Typical retiree healthcare costs: $3,000-6,000/year age 65-75, $6,000-12,000/year age 75-85, $10,000-20,000/year age 85+. Long-term care (nursing home) can cost $72,000-120,000/year with government covering only partial costs. Budget $5,000/year minimum for healthcare in your 60s-70s, increasing to $10k-15k/year in your 80s. Consider: Keeping employer health benefits through early retirement buyout, provincial drug plans (Ontario Trillium, BC Fair PharmaCare), or private insurance. Many retirees underestimate healthcare inflation (rising 5-6%/year vs 2-3% general inflation).</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Tool Recommendations */}
      <section style={{padding: '3rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          <h2 style={{textAlign: 'center', marginBottom: '0.5rem'}}>Explore More Financial Tools</h2>
          <p style={{textAlign: 'center', color: '#6b7280', marginBottom: '2rem'}}>One account, all the tools you need</p>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
            <Link to="/tax-tools" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Tax Tools</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Estimate income taxes and calculate payroll deductions</p>
              </div>
            </Link>
            <Link to="/housing" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Housing Calculator</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Compare renting vs buying with 25-year cost analysis</p>
              </div>
            </Link>
            <Link to="/forecast" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Personal Forecast</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Project your income, expenses, and net worth over 5-30 years</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Start Planning</h2>
          <p>Get clarity on your path to retirement.</p>
          <div className="cta-buttons">
            <Link to="/rrsp" className="btn btn-primary btn-large">Open Planner</Link>
            <Link to="/account" className="btn btn-secondary btn-large">Create Free Account</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetirementPlannerInfoPage; 